import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Building2, RefreshCw, Zap, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import adminService from '../../services/adminService';
import AdminNav from './AdminNav';
import { cn } from '../../lib/utils';

const PLANS = ['free', 'starter', 'professional', 'portfolio'];
const STATUSES = ['trial', 'active', 'inactive', 'suspended'];

const PLAN_COLORS = {
  free:         'bg-gray-100 text-gray-700',
  starter:      'bg-cyan-100 text-cyan-700',
  professional: 'bg-indigo-100 text-indigo-700',
  portfolio:    'bg-amber-100 text-amber-700',
};

const STATUS_COLORS = {
  active:    'bg-emerald-100 text-emerald-700',
  trial:     'bg-blue-100 text-blue-700',
  inactive:  'bg-gray-100 text-gray-500',
  suspended: 'bg-red-100 text-red-700',
};

function UsagePip({ used, max, label }) {
  if (max === null) return <span className="text-xs text-green-600">{label}: ∞</span>;
  const pct = Math.min((used / max) * 100, 100);
  const color = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-400' : 'bg-cyan-500';
  return (
    <div className="space-y-0.5">
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{label}</span>
        <span className={pct >= 100 ? 'text-red-600 font-semibold' : ''}>{used}/{max ?? '∞'}</span>
      </div>
      <div className="h-1 rounded-full bg-gray-100 overflow-hidden w-24">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const AdminOrgs = () => {
  const [orgs, setOrgs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState({});   // orgId → true
  const [editing, setEditing]       = useState({});   // orgId → { plan, status }
  const [toast, setToast]           = useState(null);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setOrgs(await adminService.getAllOrgs());
    } catch {
      showToast('error', 'Failed to load organisations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleEdit = (orgId, field, value) => {
    setEditing(prev => ({
      ...prev,
      [orgId]: { ...(prev[orgId] || {}), [field]: value },
    }));
  };

  const handleSave = async (org) => {
    const changes = editing[org._id];
    if (!changes || Object.keys(changes).length === 0) return;
    setSaving(prev => ({ ...prev, [org._id]: true }));
    try {
      await adminService.updateOrgPlan(org._id, changes);
      setOrgs(prev => prev.map(o =>
        o._id === org._id
          ? { ...o, plan: changes.plan ?? o.plan, status: changes.status ?? o.status }
          : o
      ));
      setEditing(prev => { const n = { ...prev }; delete n[org._id]; return n; });
      showToast('success', `${org.name} updated.`);
    } catch {
      showToast('error', 'Failed to save changes.');
    } finally {
      setSaving(prev => { const n = { ...prev }; delete n[org._id]; return n; });
    }
  };

  const isDirty = (orgId) => editing[orgId] && Object.keys(editing[orgId]).length > 0;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      <AdminNav />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Organisations</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Manage plan and subscription status for each org.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {toast && (
        <div className={cn(
          'flex items-center gap-2 px-4 py-3 rounded-lg text-sm border',
          toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        )}>
          {toast.type === 'success'
            ? <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
          {toast.msg}
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4 text-indigo-600" />
            {loading ? 'Loading…' : `${orgs.length} organisation${orgs.length !== 1 ? 's' : ''}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left text-xs text-muted-foreground">
                  <th className="px-4 py-2.5 font-medium">Organisation</th>
                  <th className="px-4 py-2.5 font-medium">Owner</th>
                  <th className="px-4 py-2.5 font-medium">Plan</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium">Usage</th>
                  <th className="px-4 py-2.5 font-medium">Stripe</th>
                  <th className="px-4 py-2.5 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {orgs.map(org => {
                  const currentPlan   = editing[org._id]?.plan   ?? org.plan;
                  const currentStatus = editing[org._id]?.status ?? org.status;
                  return (
                    <tr key={org._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{org.name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {new Date(org.createdAt).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-700">{org.ownerName}</p>
                        <p className="text-[11px] text-muted-foreground">{org.ownerEmail}</p>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={currentPlan}
                          onChange={e => handleEdit(org._id, 'plan', e.target.value)}
                          className="text-xs border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white"
                        >
                          {PLANS.map(p => (
                            <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                          ))}
                        </select>
                        {!editing[org._id]?.plan && (
                          <span className={cn('ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium', PLAN_COLORS[org.plan])}>
                            {org.plan}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={currentStatus}
                          onChange={e => handleEdit(org._id, 'status', e.target.value)}
                          className="text-xs border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white"
                        >
                          {STATUSES.map(s => (
                            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                          ))}
                        </select>
                        {!editing[org._id]?.status && (
                          <span className={cn('ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium', STATUS_COLORS[org.status])}>
                            {org.status}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 space-y-1">
                        <UsagePip
                          label="Props"
                          used={org.usage.unitsUsed}
                          max={org.usage.maxUnits}
                        />
                        <UsagePip
                          label="Msgs"
                          used={org.usage.messagesThisMonth}
                          max={org.usage.maxMessages}
                        />
                      </td>
                      <td className="px-4 py-3">
                        {org.stripeCustomerId ? (
                          <span className="text-[10px] text-emerald-600 font-medium">✓ Stripe</span>
                        ) : (
                          <span className="text-[10px] text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isDirty(org._id) && (
                          <div className="flex gap-1.5">
                            <Button
                              size="sm"
                              className="h-7 text-xs px-3"
                              onClick={() => handleSave(org)}
                              disabled={saving[org._id]}
                            >
                              {saving[org._id] ? 'Saving…' : 'Save'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs px-2"
                              onClick={() => setEditing(prev => { const n = { ...prev }; delete n[org._id]; return n; })}
                              disabled={saving[org._id]}
                            >
                              ✕
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!loading && orgs.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground text-sm">
                      No organisations found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminOrgs;
